import {
  AgentBookingModel,
  BookingPagingResponse,
  CancelBookingResponse,
  CancelPartialBookingResponse,
  CompleteBookingResponse,
  ConfirmPartialBookingResponse,
  PreBookHotelRequest,
} from '@/__generated';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';
import { BookingErrorType, BookingStatus, HotelRoomStatus, PaymentStatus } from '@/constants';
import { ModalUtils } from '@/contexts/ModalWrapper';
import i18n from '@/languages/i18n';
import { AppUrls } from '@/urls';
import history from '@/history';
import { handleBookingNotFound, handleSubmitError } from '@/helpers/errorHandle';
import moment from 'moment';
import { resolve } from 'path';
import React from 'react';
class BookingService {
  handleCompleteBooking = (bookingResponse: CompleteBookingResponse, onPartialBooking: () => void) => {
    if (bookingResponse.success) {
      if (bookingResponse.partialBooked) {
        onPartialBooking();
      } else if (bookingResponse.paymentStatus === PaymentStatus.NeedToCheck) {
        history.push(AppUrls.__createBookingNeedToCheckUrl(bookingResponse.bookingId || ''));
      } else {
        history.push(AppUrls.__createBookingSuccessUrl(bookingResponse.bookingId || ''));
      }
    } else {
      if (bookingResponse.sessionExpired) {
        handleBookingNotFound();
      } else if (bookingResponse.errorType === BookingErrorType.BookingProcessing) {
        ModalUtils.error({
          title: i18n.t('BOOKING_INPROGRESS_TITLE'),
          content: i18n.t('BOOKING_INPROGRESS_CONTENT'),
          onClose: () => {
            history.push(AppUrls.__createBookingDetailUrl(bookingResponse.bookingId || ''));
          },
        });
      } else if (bookingResponse.errorType === BookingErrorType.Validation) {
        ModalUtils.error({
          title: i18n.t('BOOKING_UNSUCCESSFUL'),
          content: i18n.t(bookingResponse.message || UNKNOWN_ERROR_MESSAGE),
        });
      } else if (bookingResponse.errorType === BookingErrorType.InternalException) {
        ModalUtils.error({
          title: i18n.t('BOOKING_UNSUCCESSFUL'),
          content: i18n.t(UNKNOWN_ERROR_MESSAGE),
        });
      } else if (bookingResponse.errorType === BookingErrorType.PaymentAuthorize) {
        ModalUtils.error({
          title: i18n.t('PAYMENT_FAILED'),
          content: i18n.t('BOOKING_CARD_INVALID'),
        });
      } else if (bookingResponse.errorType === BookingErrorType.ConfirmBooking) {
        history.push(AppUrls.__createBookingFailedUrl(bookingResponse.bookingId || ''));
      }
    }
  };

  handleCancelBookingResponse = (bookingId: string, response: CancelPartialBookingResponse, onModalClose: () => void) => {
    if (response.success) {
      history.push(AppUrls.__createBookingCancelledSuccessUrl(bookingId));
    } else if (response.paymentUnSuccess) {
      history.push(AppUrls.__createBookingNeedToCheckUrl(bookingId));
    } else if (response.cancellationFeeChanged) {
      ModalUtils.error({
        title: 'Cancellation fee has changed!',
        content: 'Your booking cancellation fee has changed, please check again!',
        onClose: onModalClose,
      });
    } else {
      history.push(AppUrls.__createBookingCancelledFailedUrl(bookingId));
    }
  };

  handleConfirmPartialBooking = (bookingId: string, response: ConfirmPartialBookingResponse) => {
    if (response.success) {
      if (response.paymentStatus === PaymentStatus.NeedToCheck) {
        history.push(AppUrls.__createBookingNeedToCheckUrl(bookingId || ''));
      } else {
        history.push(AppUrls.__createBookingSuccessUrl(bookingId || ''));
      }
    } else {
      handleSubmitError(response);
    }
  };

  handleOnCancelBooking = (rooms: Array<{ checkInDate: any; supplier: string; status: string }>): Promise<boolean> => {
    return new Promise(resolve => {
      let roomStatus = rooms?.map(x => x.status) || [];

      if (!roomStatus.includes(HotelRoomStatus.Booked as any)) {
        ModalUtils.error({
          title: 'Cancel booking failed',
          content: <div>The booking does not include any BOOKED/ HOLD rooms</div>,
        });
        resolve(false);
      } else if (roomStatus.includes(HotelRoomStatus.Processing as any)) {
        ModalUtils.error({
          title: 'Cancel booking failed',
          content: <div>Some rooms are still in PROCESSING</div>,
        });
        resolve(false);
      } else {
        let hasOnlyNtaRoom = rooms.some(
          x =>
            x.supplier === 'NTA' &&
            moment()
              .tz('Asia/Tokyo')
              .format('YYYY-MM-DD') === x.checkInDate,
        );
        let hasOtherSupplierRoom = rooms.some(x => x.supplier !== 'NTA');
        if (hasOnlyNtaRoom) {
          if (!hasOtherSupplierRoom) {
            ModalUtils.error({
              title: 'Cancel booking failed',
              content: (
                <div>
                  Unable to cancel NTA booking from this system on the day of check-in.
                  <br />
                  Please contact our NTA Emergency Call Center <a href="tel:+81-50-3646-9371">+81-50-3646-9371</a> (English & Japanese)
                </div>
              ),
            });
            resolve(false);
          } else {
            ModalUtils.confirm({
              title: 'Confirm cancel booking',
              onConfirm: () => {
                resolve(true);
              },
              onClose: () => {
                resolve(false);
              },
              content: (
                <div>
                  This booking includes rooms from NTA and other suppliers.
                  <br />
                  It is not possible to cancel NTA bookings through this system on the day of check-in.
                  <br /> Please contact our NTA Emergency Call Center <a href="tel:+81-50-3646-9371">+81-50-3646-9371</a> (English & Japanese)
                  <br />
                  <b> Would you like to proceed with canceling rooms from other suppliers in this booking?</b>
                </div>
              ),
            });
          }
        } else {
          resolve(true);
        }
      }
    });
  };
  canCancelOnHoldBooking = (booking: BookingPagingResponse) => {
    return booking.roomStatus?.includes(HotelRoomStatus.Booked as any);
  };
  canConfirmOnHoldBooking = (booking: BookingPagingResponse) => {
    let canConformBookingStatus = [BookingStatus.Hold, BookingStatus.PartialHold];
    if (!canConformBookingStatus.includes(booking.bookingStatus as any)) {
      return false;
    }
    return moment().isBefore(moment(booking.onHoldPaymentDeadline));
  };
  getPreBookExtraParams = () => {
    let urlParams = new URLSearchParams(window.location.search);
    let extraParams: Omit<PreBookHotelRequest, 'sessionId' | 'rooms' | 'hotelCode'> = {
      bookingRefNumber: urlParams.get('bookingRefNumber') || undefined,
    };
    return extraParams;
  };
}

export default new BookingService();
